import { useNavigate } from "react-router-dom";
import { PrimaryButton, SecondaryButton } from "../../Buttons";
import { DropdownCrossIcon } from "../../Icons";
import GetTransactionStatus from "../../RecentTranactions/partials/ViewRecentTransaction/partials/GetTransactionStatus";
import HelpModal from "../../Modals/HelpModal";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { checkClientKYCStatus } from "../../../redux/actions/lookups/checkClientKYCStatus";
import { getClientKYCLink } from "../../../redux/actions/lookups/getClientKYCLink";
import HangTightLoading from "../../Common/HangTightLoading";
import Modal from "../../Common/Modal";
import Alert from "../../Alert";

const SubAccountSideBar = ({
  account,
  onClose,
  parentClientId,
  isSumSubEnabled,
}) => {
  const navigate = useNavigate();
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [resetReason, setResetReason] = useState(null);

  const [isKycRequested, setIsKycRequested] = useState(
    account?.is_kyc_requested || 0,
  );

  const [alertConfig, setAlertConfig] = useState({
    show: false,
    message: "",
    type: "error",
  });

  useEffect(() => {
    const fetchStatus = async () => {
      if (isSumSubEnabled && account?.id) {
        const res = await dispatch(checkClientKYCStatus(account.id));
        if (res && res.success) {
          setIsKycRequested(res.kyc_requested);
        }
      }
    };
    fetchStatus();
  }, [account?.id, isSumSubEnabled, dispatch]);

  useEffect(() => {
    let interval;
    if (isSumSubEnabled) {
      if (isPolling) {
        interval = setInterval(async () => {
          const res = await dispatch(checkClientKYCStatus(account.id));
          if (res && res.success) {
            if (
              res.status === "completed" ||
              res.status === "approved" ||
              res.kyc_requested === 0
            ) {
              setIsPolling(false);
              window.location.reload();
            }
          }
        }, 20000);
      }
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [isPolling, dispatch, account?.id, isSumSubEnabled]);

  const handleSumsubKyc = async () => {
    setLoading(true);
    const res = await dispatch(getClientKYCLink(account.id));
    if (res.success) {
      setResetReason(res.reason);
      window.open(res.kyc_link, "_blank");
      setIsPolling(true);
    } else {
      setAlertConfig({
        show: true,
        message: res.message || "Unable to fetch KYC link.",
        type: "error",
      });
    }
    setLoading(false);
  };

  if (!account) return null;

  const handleResubmit = () => {
    navigate("/create-sub-account", {
      state: {
        isSubAccount: true,
        parentClientId: parentClientId,
        isRejectedUpdate: true,
        sub_client_id: account.id,
        account_type: account.type,
      },
    });
  };

  const handleContinueApplication = () => {
    if (account.status === "In Progress") {
      navigate("/create-sub-account", {
        state: {
          isSubAccount: true,
          sub_client_id: account.id,
          sub_user_id: account.user_id,
          account_type: account.type,
        },
      });
    }
  };

  return (
    <>
      {alertConfig.show && (
        <Alert
          type={alertConfig.type}
          message={alertConfig.message}
          onClose={() => setAlertConfig({ ...alertConfig, show: false })}
        />
      )}
      <div className="bg-gradient-primarycard rounded-3xl p-6 space-y-4 h-full relative">
        <div onClick={onClose} className="absolute top-4 right-4">
          <div className="hover:bg-background-hover rounded-full w-8 h-8 flex items-center justify-center cursor-pointer">
            <DropdownCrossIcon />
          </div>
        </div>

        <div className="pt-2">
          <h2 className="text-xl font-bold text-text-primary mb-1">
            {account.name}
          </h2>
          <p className="text-sm text-text-secondary uppercase tracking-wider">
            {account.type} Account
          </p>
        </div>

        <div className="py-4 border-t border-b border-divider space-y-3">
          <div className="flex justify-between items-center">
            <span className="text-sm text-text-secondary">Status</span>
            <div className="flex items-center text-text-primary">
              <GetTransactionStatus status={account.status} />
            </div>
          </div>

          <div className="flex justify-between items-center">
            <span className="text-sm text-text-secondary">Email</span>
            <span className="text-sm text-text-primary font-medium">
              {account.email || "N/A"}
            </span>
          </div>

          {account.status === "Rejected" && (
            <div className="flex justify-between items-start pt-2">
              <span className="text-sm text-text-secondary">
                Rejected Reason
              </span>
              <span className="text-sm text-error-500 font-medium bg-error-50 p-2 rounded-lg text-right max-w-[65%]">
                {resetReason || account.reject_reason || "No reason provided."}
              </span>
            </div>
          )}
        </div>

        {account.status === "Approved" && (
          <div className="pt-4">
            <p className="text-sm text-text-primary">
              Account is active and ready for use.
            </p>
          </div>
        )}

        <div className="pt-4 space-y-3">
          {isSumSubEnabled &&
            (account.status === "Rejected" ||
              (account.status === "Pending" && isKycRequested === 1)) && (
              <PrimaryButton
                text={
                  account.status === "Rejected"
                    ? "Complete Verification"
                    : "Continue Application"
                }
                onClick={handleSumsubKyc}
              />
            )}

          {!isSumSubEnabled && account.status === "Rejected" && (
            <PrimaryButton
              text="Resubmit Application"
              onClick={handleResubmit}
            />
          )}
          {account.status === "Draft" && (
            <PrimaryButton
              text="Continue Application"
              onClick={handleResubmit}
            />
          )}
          {!isSumSubEnabled && account.status === "In Progress" && (
            <PrimaryButton
              text="Continue Application"
              onClick={handleContinueApplication}
            />
          )}
          <HelpModal
            HelpModalOpen={helpModalOpen}
            setHelpModalOpen={setHelpModalOpen}
          >
            <SecondaryButton text="Get Help" />
          </HelpModal>
        </div>
      </div>

      <Modal
        isOpen={loading || isPolling}
        handleOnCloseModal={() => {}}
        header={false}
        className="w-[432px] overflow-hidden shadow-xl rounded-3xl !p-0"
      >
        <div className="px-6 max-[360px]:px-4 pb-4 pt-6">
          <HangTightLoading
            message={
              loading
                ? "Fetching secure link..."
                : "Waiting for Verification..."
            }
          />
        </div>
      </Modal>
    </>
  );
};

export default SubAccountSideBar;
